/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Box, Button, Container, Divider, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { calculatePaymentLessing, formatCurrency } from "@helpers/funciones";
import {
  usePostValidateNextStageMutation,
  useLazyGetConditionsByIdQuery,
  usePutConditionMutation,
} from "@api/applications";
import { setLoanStatus, setNewConditionLoan } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { useLazyGetTaxSystemsQuery } from "@api/catalogs";
import { LoanCondiciones } from "@interfaces/response";
import { TaxSystem } from "@interfaces/catalogs";
import { ApiError } from "@interfaces/response";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import { StyledSlider } from "@components/CustomSlider";
import { useLazyGetLeasingListQuery } from "@api/leasing";
import { LeasingStructure } from "@interfaces/LeasingTypes";
import * as Sentry from "@sentry/react";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useStatusConfig from "@hooks/useStatusConfig";

interface MarketStructure {
  value: number;
  label: string;
}

const SCREEN_STATUS = "CSAOL";

export const OfertaArrendamientoLanitAuto = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();

  const { nextStatus } = useStatusConfig(SCREEN_STATUS);

  const application = useSelector((state: RootState) => state.register.application);
  const companyData = useSelector((state: RootState) => state.register.company);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getLeasingList] = useLazyGetLeasingListQuery();
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [putCondition] = usePutConditionMutation();
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [selectedTerm, setSelectedTerm] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [anticipo, setAnticipo] = useState(30);
  const [vehicles, setVehicles] = useState<LeasingStructure | null>(null);
  const [condition, setCondition] = useState<LoanCondiciones | null>(null);
  const [taxSystems, setTaxSystems] = useState<TaxSystem | null>(null);
  const [advancePercentageMarket, setAdvancePercentageMarket] = useState<MarketStructure[]>([]);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);
  const [maxAdvancePercentage, setMaxAdvancePercentage] = useState<number>(0);
  const [minAdvancePercentage, setMinAdvancePercentage] = useState<number>(0);
  const [stepAdvancePercentage, setStepAdvancePercentage] = useState<number>(0);
  const [valorAnticipo, setValorAnticipo] = useState<number>(0);
  const [valorResidual, setValorResidual] = useState<number>(0);
  const [montoAFinanciar, setMontoAFinanciar] = useState<number>(0);
  const [isArrendamiento, setIsArrendamiento] = useState<boolean>(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);

        const [conditions, responseLeasing, taxSystems] = await Promise.all([
          getConditions(application.id).unwrap(),
          getLeasingList(String(application.id)).unwrap(),
          getTaxSystems(application.type).unwrap(),
        ]);
        const activeCondition = conditions.find((item) => item.active);
        if (activeCondition) {
          setCondition(activeCondition);
        }
        if (responseLeasing.length > 0) {
          setVehicles(responseLeasing[0]);
        }
        setTaxSystems(taxSystems);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "OfertaArrendamientoLanitAuto-initial-data",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const minTerm = condition?.approved_min_term || taxSystems?.min_term || 6;
    const maxTerm = condition?.approved_max_term || taxSystems?.max_term || 36;
    const stepTerm = taxSystems?.jump_term || 6;
    const marks = [];
    for (let i = minTerm; i <= maxTerm; i += stepTerm) {
      marks.push({ value: i, label: `${i}` });
    }

    const maxAdvance = condition?.approved_max_advance_percentage || taxSystems?.max_advance_percentage || 50;
    const minAdvance = condition?.approved_min_advance_percentage || taxSystems?.min_advance_percentage || 0;
    const stepAdvance = taxSystems?.jump_advance_percentage || 5;

    const listAdvance: MarketStructure[] = [];

    if (stepAdvance > 0 && minAdvance > 0 && maxAdvance >= minAdvance) {
      for (let r = minAdvance; r <= maxAdvance; r += stepAdvance) {
        listAdvance.push({ value: r, label: `${r}%` });
      }
      if (listAdvance.length && listAdvance[listAdvance.length - 1].value !== maxAdvance) {
        listAdvance.push({ value: maxAdvance, label: `${maxAdvance}%` });
      }
    }

    setTermMarks(marks);
    setMaxAdvancePercentage(maxAdvance);
    setMinAdvancePercentage(minAdvance);
    setStepAdvancePercentage(stepAdvance);
    setAnticipo(condition?.selected_advance_percentage || taxSystems?.default_advance_percentage || 30);
    setAdvancePercentageMarket(listAdvance);
    setSelectedTerm(condition?.selected_term || minTerm);
  }, [taxSystems, condition]);

  useEffect(() => {
    if (!condition || !vehicles?.estimated_value || !selectedTerm) {
      setMonthlyRate(0);
      setValorAnticipo(0);
      setValorResidual(0);
      setMontoAFinanciar(0);
      return;
    }

    const tasaConfig = Number(interfaceConfig?.interest_rate ?? 0);
    const iva = 0.16;
    const valorVehiculo = Number(vehicles.estimated_value) || 0;
    const plazo = Number(selectedTerm) || 0;

    if (!tasaConfig || !valorVehiculo || !plazo) {
      setMonthlyRate(0);
      setValorAnticipo(0);
      setValorResidual(0);
      setMontoAFinanciar(0);
      return;
    }

    
    const esArrendamiento =  Number(vehicles.requested_residual_percentage || 0) > 0;
    const residualPercentage = Number(vehicles.requested_residual_percentage ?? vehicles.selected_residual_percentage ?? 20);
    setIsArrendamiento(esArrendamiento);

    if (esArrendamiento) {
      const tasaMensual = tasaConfig / 12 / 100;

      // Regla de arrendamiento: anticipo y residual se calculan sobre base sin IVA.
      const montoSinIva = valorVehiculo / (1 + iva);
      const valorAnticipoSinIva = montoSinIva * (anticipo / 100);
      const valorResidualSinIva = montoSinIva * (residualPercentage / 100);
      const montoFinanciarSinIva = montoSinIva - valorAnticipoSinIva;

      const montoFinanciarConIva = montoFinanciarSinIva * (1 + iva);
      const valorResidualConIva = valorResidualSinIva * (1 + iva);

      const pagoMensual = calculatePaymentLessing(tasaMensual, plazo, -montoFinanciarConIva, valorResidualConIva);

      setMonthlyRate(pagoMensual);
      setValorAnticipo(valorAnticipoSinIva);
      setValorResidual(valorResidualSinIva);
      setMontoAFinanciar(montoFinanciarSinIva);
      return;
    }

    // Regla de crédito automotriz: la tasa configurada viene como tasa mensual porcentual.
    const tasaMensualCredito = tasaConfig / 100;
    const valorAnticipoCredito = valorVehiculo * (anticipo / 100);
    const montoFinanciar = valorVehiculo - valorAnticipoCredito;
    const pagoMensual = calculatePaymentLessing(tasaMensualCredito, plazo, -montoFinanciar, 0);

    setMonthlyRate(pagoMensual);
    setValorAnticipo(valorAnticipoCredito);
    setValorResidual(0);
    setMontoAFinanciar(montoFinanciar);
  }, [anticipo, condition, interfaceConfig?.interest_rate, selectedTerm, vehicles?.estimated_value]);

  const handleOnClickUpdateConditionLoan = async () => {
    if (!condition || !application) return;

    try {
      setIsLoading(true);

      const data = {
        loan_application: application.id,
        loan_amount: vehicles?.estimated_value || 0,
        requested_term: selectedTerm || 0,
        selected_amount: vehicles?.estimated_value || 0,
        selected_term: selectedTerm || 0,
        requested_amount: vehicles?.estimated_value || 0,
        selected_advance_percentage: anticipo || 0,
        selected_residual_percentage: condition?.selected_residual_percentage || 0,
        user_name: `TOFU - ${accountUser.email}`,
        user_id: String(accountUser.id),
      };
      await putCondition({ body: data, id: String(condition?.id) }).unwrap();

      dispatch(
        setNewConditionLoan({
          approved_amount: condition.approved_amount,
          approved_term: condition.approved_term,
          interest_rate: condition.interest_rate,
          loanAmount: vehicles?.estimated_value || 0,
          loanTerm: condition?.selected_term || 0,
          amountMonthly: monthlyRate,
        }),
      );

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus?.code || "",
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setLoanStatus(nextStatus?.code || ""));

      navigate(nextStatus?.url || "");
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} md={12} mt={5} sx={{ position: "relative" }}>
          <InactivityHandler />
          <Container maxWidth={"md"}>
            <Box
              sx={{
                my: 0,
                mx: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box sx={{ mt: 0, mb: 2 }}>
                <Typography
                  color={theme?.palette?.primary?.dark}
                  fontWeight={theme?.palette?.fontWeights?.medium}
                  align="center"
                  fontSize={"22px"}
                >
                  ¡Felicidades!
                  <br />
                  {companyData.name}
                </Typography>
              </Box>

              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography
                  color={theme?.palette?.primary?.dark}
                  fontWeight={theme?.palette?.fontWeights?.light}
                  align="center"
                  fontSize={"15px"}
                >
                  Tu financiamiento ha sido autorizado
                </Typography>

                <Typography
                  color={theme?.palette?.primary?.main}
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  align="center"
                  fontSize={"22px"}
                  mt={2}
                ></Typography>
              </Box>

              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography
                  color={theme?.palette?.primary?.dark}
                  fontWeight={theme?.palette?.fontWeights?.light}
                  align="center"
                  fontSize={"15px"}
                >
                  Vehículo:
                </Typography>

                <Typography
                  color={theme?.palette?.primary?.dark}
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  align="center"
                  fontSize={"15px"}
                >
                  {vehicles ? (
                    `${vehicles?.details?.make} ${vehicles?.details?.model} ${vehicles?.details?.version} ${vehicles?.details?.year}`
                  ) : (
                    <Skeleton variant="text" width={150} />
                  )}
                </Typography>
              </Box>

              <Typography
                fontWeight={500}
                fontSize={"16px"}
                mt={2}
                mb={2}
                color={theme?.palette?.primary?.light}
                textAlign={"center"}
              >
                Confirma el plazo en meses
              </Typography>
              {!isLoading ? (
                <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1.5}>
                  {termMarks.map((term) => {
                    return (
                      <ButtonTypeSelectedCustom
                        key={term.value}
                        title={`${term.label}`}
                        onClick={() => setSelectedTerm(term.value)}
                        isSelected={selectedTerm === term.value}
                        isMovile={isMobileOrTablet}
                        height="44px"
                        heightMoviles="40px"
                        width={"48px"}
                        widthMoviles={"48px"}
                        font_size={"12px"}
                        font_size_movil={"12px"}
                        bgColor={theme.palette.primary.main}
                        bgColorActive={theme.palette.primary.dark}
                        textColor={theme.palette.primary.dark}
                        hoverBgColor={theme.palette.primary.dark}
                      />
                    );
                  })}
                </Box>
              ) : (
                <Skeleton variant="rectangular" animation="wave" />
              )}

              <Container maxWidth={"xs"}>
                <Box sx={{ mt: 4, mb: 2 }}>
                  <Typography color={theme?.palette?.primary?.dark} align="center" variant="body1">
                    Confirma el anticipo
                  </Typography>
                </Box>
                {!isLoading ? (
                  <StyledSlider
                    aria-label="advance"
                    min={minAdvancePercentage}
                    max={maxAdvancePercentage}
                    step={stepAdvancePercentage}
                    defaultValue={anticipo}
                    marks={advancePercentageMarket}
                    value={anticipo}
                    onChange={(_event, newValue) => setAnticipo(newValue as number)}
                    valueLabelDisplay="on"
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Container>

              {isArrendamiento ? (
                <>
                  {/* arredamiento */}
                  <Box display={"flex"} flexDirection={"row"} mt={4} mb={4}>
                    <Box width={"45%"}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={400}
                        textAlign={"center"}
                        color={theme?.palette?.primary?.dark}
                      >
                        Valor del anticipo
                      </Typography>
                      <Typography
                        fontSize={"18px"}
                        fontWeight={600}
                        textAlign={"center"}
                        mt={1}
                        color={theme?.palette?.primary?.dark}
                      >
                        {formatCurrency(valorAnticipo || 0)}{" "}
                        <Typography variant="body3" component="span" fontWeight={500}>
                          {interfaceConfig.cotizador?.current_currency || "MXN"}
                        </Typography>
                      </Typography>
                    </Box>

                    <Box width={"10%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ mr: "-2px", border: `1px solid ${theme?.palette?.primary?.dark}` }}
                      />
                    </Box>

                    <Box width={"45%"}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={400}
                        textAlign={"center"}
                        color={theme?.palette?.primary?.dark}
                      >
                        Valor del residual
                      </Typography>

                      <Typography
                        fontSize={"18px"}
                        fontWeight={600}
                        textAlign={"center"}
                        color={theme?.palette?.primary?.dark}
                        mt={1}
                      >
                        {formatCurrency(valorResidual || 0)}{" "}
                        <Typography variant="body3" component="span" fontWeight={500}>
                          {interfaceConfig.cotizador?.current_currency || "MXN"}
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>

                  <Box display={"flex"} flexDirection={"row"} mt={1} mb={2}>
                    <Box width={"45%"}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={400}
                        textAlign={"center"}
                        color={theme?.palette?.primary?.dark}
                      >
                        Valor del vehiculo
                      </Typography>
                      <Typography
                        fontSize={"18px"}
                        fontWeight={600}
                        textAlign={"center"}
                        mt={1}
                        color={theme?.palette?.primary?.dark}
                      >
                        {formatCurrency(vehicles?.estimated_value || 0)}{" "}
                        <Typography variant="body3" component="span" fontWeight={500}>
                          {interfaceConfig.cotizador?.current_currency || "MXN"}
                        </Typography>
                      </Typography>
                    </Box>

                    <Box width={"10%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ mr: "-2px", border: `1px solid ${theme?.palette?.primary?.dark}` }}
                      />
                    </Box>

                    <Box width={"45%"}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={400}
                        textAlign={"center"}
                        color={theme?.palette?.primary?.dark}
                      >
                        Monto a financiar
                      </Typography>

                      <Typography
                        fontSize={"18px"}
                        fontWeight={600}
                        textAlign={"center"}
                        color={theme?.palette?.primary?.dark}
                        mt={1}
                      >
                        {formatCurrency(montoAFinanciar || 0)}{" "}
                        <Typography variant="body3" component="span" fontWeight={500}>
                          {interfaceConfig.cotizador?.current_currency || "MXN"}
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  {/* credito automotriz */}
                  <Box mt={4} mb={4} display={"flex"} justifyContent={"center"}>
                    <Box width={isMobileOrTablet ? "100%" : "45%"}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={400}
                        textAlign={"center"}
                        color={theme?.palette?.primary?.dark}
                      >
                        Valor del anticipo
                      </Typography>
                      <Typography
                        fontSize={"18px"}
                        fontWeight={600}
                        textAlign={"center"}
                        mt={1}
                        color={theme?.palette?.primary?.dark}
                      >
                        {formatCurrency(valorAnticipo || 0)}{" "}
                        <Typography variant="body3" component="span" fontWeight={500}>
                          {interfaceConfig.cotizador?.current_currency || "MXN"}
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>

                  <Box display={"flex"} flexDirection={"row"} mt={1} mb={2}>
                    <Box width={"45%"}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={400}
                        textAlign={"center"}
                        color={theme?.palette?.primary?.dark}
                      >
                        Valor del vehiculo
                      </Typography>
                      <Typography
                        fontSize={"18px"}
                        fontWeight={600}
                        textAlign={"center"}
                        mt={1}
                        color={theme?.palette?.primary?.dark}
                      >
                        {formatCurrency(vehicles?.estimated_value || 0)}{" "}
                        <Typography variant="body3" component="span" fontWeight={500}>
                          {interfaceConfig.cotizador?.current_currency || "MXN"}
                        </Typography>
                      </Typography>
                    </Box>

                    <Box width={"10%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ mr: "-2px", border: `1px solid ${theme?.palette?.primary?.dark}` }}
                      />
                    </Box>

                    <Box width={"45%"}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={400}
                        textAlign={"center"}
                        color={theme?.palette?.primary?.dark}
                      >
                        Monto a financiar
                      </Typography>

                      <Typography
                        fontSize={"18px"}
                        fontWeight={600}
                        textAlign={"center"}
                        color={theme?.palette?.primary?.dark}
                        mt={1}
                      >
                        {formatCurrency(montoAFinanciar || 0)}{" "}
                        <Typography variant="body3" component="span" fontWeight={500}>
                          {interfaceConfig.cotizador?.current_currency || "MXN"}
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}

              <Typography
                fontWeight={700}
                fontSize={"16px"}
                color={theme?.palette?.primary?.dark}
                textAlign={"center"}
                mt={2}
              >
                Pago mensual estimado*
              </Typography>

              <Typography
                mb={2}
                textAlign={"center"}
                variant="h4"
                fontWeight={theme?.palette?.fontWeights?.semibold}
                color={theme?.palette?.primary?.dark}
              >
                {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                <Typography
                  variant="h5"
                  component={"span"}
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  color={theme?.palette?.primary?.dark}
                >
                  MXN
                </Typography>
              </Typography>
              <Box sx={{ mt: 1, mb: 1 }}>
                <Button variant="default" onClick={handleOnClickUpdateConditionLoan}>
                  Continuar
                </Button>
              </Box>
            </Box>
          </Container>
        </Grid>
      </Grid>
    </>
  );
};
